import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  signInWithPopup,
  signInWithRedirect,
  onAuthStateChanged,
  signOut as fbSignOut,
} from 'firebase/auth';
import { auth, googleProvider, firebaseEnabled } from '../lib/firebase.js';

const AuthContext = createContext(null);
const STORAGE_KEY = 'pubgolf.user';

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function makeGuestId() {
  // Short prefix + random — readable but unique per device.
  const rand =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `guest_${rand.replace(/-/g, '').slice(0, 16)}`;
}

/** Convert a Firebase user object into our local shape. */
function fromFirebaseUser(fbUser) {
  if (!fbUser) return null;
  return {
    uid: fbUser.uid,
    displayName: fbUser.displayName || fbUser.email?.split('@')[0] || 'Pub Golfer',
    email: fbUser.email || null,
    photoURL: fbUser.photoURL || null,
    isGuest: false,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(loadStored);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Persist whatever the current user state is to localStorage so the app
  // boots into the previous session before Firebase resolves.
  useEffect(() => {
    if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEY);
  }, [user]);

  // Subscribe to Firebase auth state changes (handles both popup completion
  // and silent re-auth on refresh). Guests are managed locally and ignored.
  useEffect(() => {
    if (!firebaseEnabled || !auth) return;
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        const next = fromFirebaseUser(fbUser);
        // Don't clobber existing local fields the app may have added later
        // (e.g. city/bio from profile updates).
        setUser((prev) => {
          if (prev?.uid === next.uid) return { ...prev, ...next };
          return next;
        });
      } else {
        // Only clear if the previous session was a real Firebase user —
        // never wipe a local guest session here.
        setUser((prev) => (prev && !prev.isGuest ? null : prev));
      }
    });
    return () => unsub();
  }, []);

  const signInAsGuest = useCallback(() => {
    const id = makeGuestId();
    const next = {
      uid: id,
      displayName: 'Guest',
      email: null,
      photoURL: null,
      isGuest: true,
    };
    setUser(next);
    return next;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    if (!firebaseEnabled || !auth || !googleProvider) {
      const msg =
        'Google sign-in is not configured. Set VITE_FIREBASE_API_KEY in .env and restart the dev server.';
      setError(msg);
      throw new Error(msg);
    }
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const next = fromFirebaseUser(result.user);
      setUser(next);
      return next;
    } catch (err) {
      // Popup blocked / closed → fall back to redirect flow.
      const code = err?.code || '';
      if (
        code === 'auth/popup-blocked' ||
        code === 'auth/operation-not-supported-in-this-environment'
      ) {
        try {
          await signInWithRedirect(auth, googleProvider);
          // Redirect navigates away — nothing else to do here.
          return null;
        } catch (redirectErr) {
          setError(redirectErr.message || 'Redirect sign-in failed');
          throw redirectErr;
        }
      }
      // User-cancelled popups should fail quietly.
      if (
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request'
      ) {
        setLoading(false);
        return null;
      }
      setError(err.message || 'Google sign-in failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    // Always clear local state.
    setUser(null);
    // Best-effort Firebase sign-out — only if Firebase is initialized.
    if (firebaseEnabled && auth) {
      try {
        await fbSignOut(auth);
      } catch {}
    }
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        firebaseEnabled,
        signInAsGuest,
        signInWithGoogle,
        signOut,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
